import { useCallback, useEffect, useState } from 'react';

interface PresenceTransitionOptions {
  activationFrames?: 1 | 2;
  duration?: number;
}

const prefersReducedMotion = (): boolean => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

export const usePresenceTransition = (
  isOpen: boolean,
  options: PresenceTransitionOptions = {},
) => {
  const activationFrames = options.activationFrames ?? 2;
  const duration = options.duration ?? 160;
  const reducedMotion = prefersReducedMotion();
  const resolvedDuration = reducedMotion ? 0 : duration;
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isActive, setIsActive] = useState(false);
  const exitImmediately = useCallback(() => {
    setIsActive(false);
    setShouldRender(false);
  }, []);

  useEffect(() => {
    let initialAnimationFrameId = 0;
    let activationAnimationFrameId = 0;
    let timeoutId = 0;

    if (isOpen) {
      setShouldRender(true);
      if (resolvedDuration === 0) {
        setIsActive(true);
      } else if (activationFrames === 1) {
        // Dropdowns start on the next paint to avoid a perceptible pause.
        activationAnimationFrameId = window.requestAnimationFrame(() => {
          setIsActive(true);
        });
      } else {
        // Other overlays retain a fully painted inactive frame before entry.
        initialAnimationFrameId = window.requestAnimationFrame(() => {
          activationAnimationFrameId = window.requestAnimationFrame(() => {
            setIsActive(true);
          });
        });
      }
    } else {
      setIsActive(false);
      if (resolvedDuration === 0) {
        setShouldRender(false);
      } else {
        timeoutId = window.setTimeout(() => {
          setShouldRender(false);
        }, resolvedDuration);
      }
    }

    return () => {
      if (initialAnimationFrameId) {
        window.cancelAnimationFrame(initialAnimationFrameId);
      }
      if (activationAnimationFrameId) {
        window.cancelAnimationFrame(activationAnimationFrameId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [activationFrames, isOpen, resolvedDuration]);

  return {
    shouldRender,
    isActive,
    duration: resolvedDuration,
    exitImmediately,
  };
};
